import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DockPosition } from '../assistant/types';

export const MIN_WIDTH = 330;
export const MIN_HEIGHT = 400;

export type ResizeAxis = 'v' | 'h' | 'c';

interface Size {
  width: number;
  height: number;
}

const STORAGE_KEY = 'assistant-dock.size';

function readStored(): Size | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Size>;
    if (typeof parsed.width === 'number' && typeof parsed.height === 'number') {
      return { width: parsed.width, height: parsed.height };
    }
  } catch {
    // Private mode, blocked storage, or a corrupt value. The configured
    // default is a perfectly good answer.
  }
  return null;
}

function writeStored(size: Size): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(size));
  } catch {
    // Size just won't survive a reload.
  }
}

/**
 * Dock sizing.
 *
 * The panel is pinned at a configured corner and grows toward the open space,
 * so which direction counts as "bigger" depends on the anchor: pinned right, a
 * drag to the left must widen it; pinned left, the same drag must narrow it.
 * That is the whole reason for the sign factors below.
 *
 * The configured size is the default and a user drag overrides it, so changing
 * config does not fight a stored value the user chose deliberately.
 */
export function useDockSize(
  position: DockPosition,
  configured: Size,
  offset: number,
  resizable: boolean,
) {
  const clamp = useCallback(
    (size: Size): Size => {
      const maxWidth = Math.max(MIN_WIDTH, window.innerWidth - offset * 2);
      const maxHeight = Math.max(MIN_HEIGHT, window.innerHeight - offset * 2);
      return {
        width: Math.min(Math.max(size.width, MIN_WIDTH), maxWidth),
        height: Math.min(Math.max(size.height, MIN_HEIGHT), maxHeight),
      };
    },
    [offset],
  );

  const [size, setSize] = useState<Size>(() => clamp(readStored() ?? configured));
  const [resizing, setResizing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const dragRef = useRef<{ axis: ResizeAxis; x: number; y: number; size: Size } | null>(null);

  // Keep the dock inside the viewport when the window shrinks under it.
  useEffect(() => {
    const onResize = () => setSize((current) => clamp(current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  const onPointerDown = useCallback(
    (axis: ResizeAxis) => (event: React.PointerEvent<HTMLDivElement>) => {
      if (!resizable) return;
      dragRef.current = { axis, x: event.clientX, y: event.clientY, size };
      event.currentTarget.setPointerCapture(event.pointerId);
      setResizing(true);
      event.preventDefault();
    },
    [resizable, size],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;

      const signX = position.includes('right') ? -1 : 1;
      const signY = position.includes('bottom') ? -1 : 1;

      setSize(
        clamp({
          width: drag.axis === 'v' ? drag.size.width : drag.size.width + signX * (event.clientX - drag.x),
          height: drag.axis === 'h' ? drag.size.height : drag.size.height + signY * (event.clientY - drag.y),
        }),
      );
    },
    [clamp, position],
  );

  const onPointerUp = useCallback(() => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setResizing(false);
    setExpanded(false);
    setSize((current) => {
      writeStored(current);
      return current;
    });
  }, []);

  const toggleExpanded = useCallback(() => {
    setExpanded((wasExpanded) => {
      const next = wasExpanded
        ? configured
        : { width: Math.round(configured.width * 1.4), height: Math.round(configured.height * 1.3) };
      const clamped = clamp(next);
      setSize(clamped);
      writeStored(clamped);
      return !wasExpanded;
    });
  }, [clamp, configured]);

  return {
    size,
    resizing,
    expanded,
    toggleExpanded,
    handleProps: { onPointerDown, onPointerMove, onPointerUp },
  };
}
